"use client";

import { useState } from "react";
import Link from "next/link";
import { createTranslator } from "@/lib/localization";
import { forgotPassword } from "@/lib/account-api";

type Props = {
  activeLanguage: string;
  texts?: Record<string, string> | null;
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ForgotPasswordForm({ activeLanguage, texts }: Props) {
  const t = createTranslator(texts ?? {});
  const langPrefix = activeLanguage !== "en" ? `/${activeLanguage}` : "";

  const [email, setEmail] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [status, setStatus] = useState<"idle" | "submitting" | "done">("idle");

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!EMAIL_PATTERN.test(email.trim())) {
      setFieldError(t("forgotPassword.invalidEmail"));
      return;
    }
    setFieldError(null);
    setStatus("submitting");
    await forgotPassword(email.trim(), activeLanguage);
    // Always the same next state regardless of whether the email belongs to an account —
    // see backend CustomerAccountService.RequestPasswordResetAsync remarks.
    setStatus("done");
  }

  return (
    <section style={styles.section}>
      <div style={styles.card}>
        <h1 style={styles.title}>{t("forgotPassword.title")}</h1>

        {status === "done" ? (
          <>
            <p style={styles.successMessage}>{t("forgotPassword.checkEmail")}</p>
            <Link href={`${langPrefix}/login`} style={styles.link}>
              {t("forgotPassword.backToLogin")}
            </Link>
          </>
        ) : (
          <form onSubmit={handleSubmit} style={styles.form} noValidate>
            <p style={styles.description}>{t("forgotPassword.description")}</p>
            <label style={styles.label}>
              {t("forgotPassword.emailLabel")}
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={styles.input}
              />
              {fieldError ? <span style={styles.fieldError}>{fieldError}</span> : null}
            </label>

            <button type="submit" disabled={status === "submitting"} style={styles.submitButton}>
              {status === "submitting" ? t("forgotPassword.submitting") : t("forgotPassword.submit")}
            </button>

            <div style={styles.footer}>
              <Link href={`${langPrefix}/login`} style={styles.link}>
                {t("forgotPassword.backToLogin")}
              </Link>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

const styles: Record<string, React.CSSProperties> = {
  section: { marginTop: 16, padding: "0 16px 48px" },
  card: {
    maxWidth: 460,
    margin: "0 auto",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: "1.5rem",
    background: "#fff",
    textAlign: "center" as const,
  },
  title: { margin: "0 0 0.8rem", fontSize: "1.5rem", color: "#0f172a", fontWeight: 700 },
  description: { margin: "0 0 0.4rem", fontSize: "0.88rem", color: "#64748b", textAlign: "left" as const },
  form: { display: "grid", gap: 14, textAlign: "left" as const },
  label: { display: "grid", gap: 4, fontSize: "0.85rem", color: "#334155", fontWeight: 600 },
  input: {
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    padding: "0.6rem 0.75rem",
    fontSize: "0.95rem",
    width: "100%",
    boxSizing: "border-box" as const,
  },
  fieldError: { fontSize: "0.78rem", color: "#dc2626" },
  successMessage: { margin: "0 0 1rem", fontSize: "0.95rem", color: "#15803d", fontWeight: 600 },
  submitButton: {
    border: "none",
    borderRadius: 8,
    background: "#2563eb",
    color: "#fff",
    fontWeight: 700,
    padding: "0.75rem 1rem",
    fontSize: "1rem",
    cursor: "pointer",
  },
  footer: { marginTop: "0.4rem", textAlign: "center" as const, fontSize: "0.88rem", color: "#64748b" },
  link: { color: "#2563eb", fontWeight: 600, textDecoration: "none" },
};
